"use strict";

/* global dataLayer */

import tabs from "../tabs";
import Loop54Search from "./loop54-search";
import { openingToNewTab, throttle as simpleThrottle } from "../helpers";
import { getProductDataLayerData, updateProductListItemPositions } from "../product-list-item";
import GTMHelper from "../gtm-helper";

global._searchWaitTimers = global._searchWaitTimers || {};
global._searchThrottleWaitTimers = global._searchThrottleWaitTimers || {};

function waitForFinalEvent(callback, ms, uniqueId) {
	if (global._searchWaitTimers[uniqueId]) {
		clearTimeout(global._searchWaitTimers[uniqueId]);
	}
	global._searchWaitTimers[uniqueId] = setTimeout(callback, ms);
}

function throttle(func, limit, uniqueId) {

	if (typeof global._searchThrottleWaitTimers[uniqueId] === "undefined") {
		global._searchThrottleWaitTimers[uniqueId] = {};
	}

	if (!global._searchThrottleWaitTimers[uniqueId].lastRan) {
		func.apply();
		global._searchThrottleWaitTimers[uniqueId].lastRan = Date.now();
		return func;
	} else {
		clearTimeout(global._searchThrottleWaitTimers[uniqueId].last);
		global._searchThrottleWaitTimers[uniqueId].last = setTimeout(function () {
			if (global._searchThrottleWaitTimers[uniqueId] && (Date.now() - global._searchThrottleWaitTimers[uniqueId].lastRan) >= limit) {
				func.apply();
				global._searchThrottleWaitTimers[uniqueId].lastRan = Date.now();
			}
		}, limit - (Date.now() - global._searchThrottleWaitTimers[uniqueId].lastRan));
	}
}

class Loop54SearchUi {

	addPageListeners(state, settings, loop54Search) {

		if (state.page) {

			window.addEventListener("pageshow", (event) => {
				if (event.persisted) {
					for (const resultsArea of [state?.views?.products?.elems?.results, state?.views?.products?.elems?.relatedResults]) {
						if (!resultsArea) {
							continue;
						}
						const productItems = resultsArea.querySelectorAll(".product-list-item.clicked-search-item");
						for (const productItem of productItems) {
							productItem.classList.add("item-revealed");
							productItem.classList.remove("clicked-search-item");
						}
					}

				}
			});

			let productImpressions = [];
			let impressionsTimeout = null;

			const handleLazyLoad = (e) => {

				const productListItem = e.target.closest(".product-list-item");

				if (productListItem) {

					productListItem.classList.add("item-revealed");

					const dataLayerData = getProductDataLayerData(productListItem);

					if (dataLayerData) {
						productImpressions.push(dataLayerData);
					}

					if (impressionsTimeout) {
						clearTimeout(impressionsTimeout);
					}

					impressionsTimeout = setTimeout(() => {
						if (GTMHelper.isEnabled() && productImpressions.length > 0) {
							GTMHelper.productImpressions(productImpressions);
							productImpressions = [];
						}
					}, 2000);
				}

			};

			if (!settings.isCategory) {
				if (state.views.articles.elems.results) {
					state.views.articles.elems.results.addEventListener("click", function () {
						loop54Search.saveSearchState();
					}, { passive: true });
				}

				if (state.views.shops.elems.results) {
					state.views.shops.elems.results.addEventListener("click", function () {
						loop54Search.saveSearchState();
					}, { passive: true });
				}
			}

			if (state.views.products.elems.results) {
				state.views.products.elems.results.addEventListener("lazyloaded", handleLazyLoad, { passive: true });
			}

			if (!settings.isCategory) {
				if (state.views.products.elems.relatedResults) {
					state.views.products.elems.relatedResults.addEventListener("lazyloaded", handleLazyLoad, { passive: true });
				}

				const handleSuggestedQueries = (e) => {
					e.preventDefault();
					const target = e.target;
					if (target.dataset.suggestion && state.searchBoxes.length > 0) {
						for (const searchBoxes of state.searchBoxes) {
							searchBoxes.value = target.dataset.suggestion;
						}
						waitForFinalEvent(() => {
							loop54Search.handleSearch({ clear: true, clearScope: true, keepFilters: false, newSearch: true });
						}, settings.showSuggestionsAfter, "search");
					}
				};

				if (state.relatedQueries) {
					state.relatedQueries.addEventListener("click", handleSuggestedQueries);
				}

				if (state.spellingSuggestions) {
					state.spellingSuggestions.addEventListener("click", handleSuggestedQueries);
				}
			}
			const handleProductClick = (e, skipAction) => {

				const newTab = openingToNewTab(e);
				if (!newTab) {
					// Prevent actual click event if not opening to new tab (so that datalayer has the time to receive the event)
					// Opening to new tab is allowed, because tag manager event will not be interrubted
					e.preventDefault();
				} else {
					skipAction = true;
					e.stopPropagation();
				}

				const productItem = e.target.closest(".product-list-item");
				if (!productItem) {
					return;
				}

				if (productItem.getAttribute("href") &&
					(productItem.tagName === "A") &&
					productItem.classList.contains("product-list-promo")) {
					if (!skipAction) {
						loop54Search.saveSearchState();
						window.location.href = productItem.href;
					} else {
						return;
					}
				} else if (productItem.classList.contains("product-list-promo")) {
					return;
				}

				if (!skipAction) {
					loop54Search.saveSearchState();
				}


				const productLink = productItem.querySelector("a.product-link");
				const navigateLink = productLink ? productLink.href : false;
				const navigateToUrl = () => {
					if (!skipAction) {
						window.location.href = navigateLink;
					}
				};
				if (productItem && navigateLink) {

					if (!skipAction) {
						productItem.classList.remove("item-revealed");
					}

					productItem.classList.add("clicked-search-item");
					const identifier = productItem.dataset.identifier;
					const type = productItem.dataset.type;
					const category = productItem.dataset.category;
					if (identifier && type) {

						const entity = { type: type, id: identifier };

						const eventCategory = settings.isCategory ? "Loop54Category" : "Loop54Search";

						if (dataLayer) {
							dataLayer.push({
								event: "GAEvent",
								eventCategory: eventCategory,
								eventAction: "Result page click",
								eventLabel: category ? category + ":" + identifier : identifier
							});

							dataLayer.push({
								event: "GAEvent",
								eventCategory: eventCategory,
								eventAction: "Scroll depth",
								eventLabel: window.scrollY
							});
						}

						if (state.client) {

							if (GTMHelper.isEnabled() && productItem.dataset.id && productItem.dataset.color) {
								if (!productItem.getAttribute("data-position")) {
									updateProductListItemPositions();
								}
								const loopEvent = {
									click: () => {
										state.client.createEvent("click", entity, () => {
											navigateToUrl();
										});
									}
								};

								const dataLayerData = getProductDataLayerData(productItem);

								if (dataLayerData) {
									GTMHelper.pushClickProduct(dataLayerData, () => {
										// Ensures that click event happens only once even if there are multiple GTM containers.
										if (typeof loopEvent.click === "function") {
											loopEvent.click();
											loopEvent.click = false;
										}
									});
								}
							} else {
								state.client.createEvent("click", entity, () => {
									navigateToUrl();
								});
							}

						} else {
							// This should never happen.
							navigateToUrl();
						}

					} else {
						navigateToUrl();
					}
				} else if (navigateLink) {
					navigateToUrl();
				}
			};

			if (state.views.products.elems.results) {
				state.views.products.elems.results.addEventListener("click", handleProductClick);
				state.views.products.elems.results.addEventListener("auxclick", (e) => {
					if (e.button && e.button === 1) {
						handleProductClick(e, true);
					}
				});
			}

			const views = settings.isCategory ? ["products"] : Object.keys(state.views);

			if (!settings.isCategory && state.views.products.elems.relatedResults) {
				state.views.products.elems.relatedResults.addEventListener("click", handleProductClick);
			}

			for (let view of views) {

				// Add event listeners for tab changes
				if (!settings.isCategory) {
					state.views[view].elems.elem.addEventListener("tab.open", (e) => {
						state.activeView = view;
					});
				}

				if (state.views[view].elems.showMoreResults) {
					const showMoreButton = state.views[view].elems.showMoreResults.querySelector(".js-load-more-results");
					if (showMoreButton) {

						showMoreButton.addEventListener("click", (e) => {
							e.preventDefault();
							waitForFinalEvent(() => {
								loop54Search.handleSearch({ activeView: view, clear: false, clearScope: false, keepFilters: true, newSearch: false });
							}, settings.showSuggestionsAfter, "search");
						});
					}
				}
			}
		}

	}


	addSearchBoxListeners(state, settings, loop54Search) {

		const ignoreKeys = ["PageUp", "PageDown", "Home", "End", "Left", "Right", "ArrowLeft", "ArrowRight", "Up", "ArrowUp", "Dead"];

		const newSearchAnalyticsEvent = () => {
			if (state.searchBoxDesktop && dataLayer) {
				dataLayer.push({
					event: "GAEvent",
					eventCategory: "Loop54Search",
					eventAction: "New search",
					eventLabel: String(state.searchBoxDesktop.value).trim()
				});
			}
		};

		const handleSearch = (clear, clearScope) => {
			loop54Search.handleSearch({ clear: clear, clearScope: clearScope, keepFilters: false, newSearch: true, redirectToSearch: settings.isCategory });
			window.scroll(0, 0);
		};

		if (state.suggestions && state.searchBoxes.length > 0) {

			for (const suggestionBox of state.suggestions) {

				suggestionBox.addEventListener("keydown", (e) => {
					const handleKeyNavigation = (mode = "down") => {
						e.preventDefault();
						const active = document.activeElement;

						const items = Array.from(suggestionBox.querySelectorAll(".suggested-item, .suggested-search")).filter(item => {
							if (getComputedStyle(item).display === "none") {
								return false;
							}

							return true;
						});

						if (items.length === 0) {
							return false;
						}

						let currentIndex = 0;
						items.some((item, index) => {
							if (item === active) {
								currentIndex = index;
								return true;
							} else {
								return false;
							}
						});

						if (mode === "down") {
							const nextIndex = currentIndex + 1;
							if (nextIndex > items.length - 1) {
								if (items[0]) {
									items[0].focus();
								}
							} else {
								items[nextIndex].focus();
							}
						} else {
							const nextIndex = currentIndex - 1;
							if (nextIndex < 0) {
								const wrapper = suggestionBox.closest(".search-box-wrapper");
								if (wrapper) {
									const searchBox = wrapper.querySelector(".search-box");
									if (searchBox) {
										searchBox.focus();
									}
								}
							} else {
								items[nextIndex].focus();
							}
						}

						return true;
					};

					const handleEsc = () => {
						const wrapper = suggestionBox.closest(".search-box-wrapper");
						if (wrapper) {
							const searchBox = wrapper.querySelector(".search-box");
							if (searchBox) {
								searchBox.focus();
							}
						}
						/*
						loop54Search.clearSuggestions();
						for (const searchBox of workset.searchBoxes) {
							searchBox.value = '';
						} */
					};

					switch (e.key) {
						case "Up":
						case "ArrowUp":
							handleKeyNavigation("up");
							return;
						case "Down":
						case "ArrowDown":
							handleKeyNavigation("down");
							return;
						case "Esc":
						case "Escape":
							handleEsc();

							return;
					}

				});

				suggestionBox.addEventListener("click", (e) => {
					e.preventDefault();
					let target = e.target;

					if (!target.classList.contains("suggested-item") || !target.classList.contains("suggested-search")) {
						target = target.closest(".suggested-item, .suggested-search");
					}

					if (target && target.dataset.suggestion) {
						for (const searchBox of state.searchBoxes) {
							searchBox.value = target.dataset.suggestion;
						}
						if (dataLayer) {
							dataLayer.push({
								event: "GAEvent",
								eventCategory: "Loop54Search",
								eventAction: "Quick search click",
								eventLabel: "Search suggestion: " + target.dataset.suggestion
							});
						}

						waitForFinalEvent(() => {
							handleSearch(true, true);
						}, settings.showSuggestionsAfter, "search");
					} else if (target && target.dataset.scopedSuggestion && target.dataset.scope && target.dataset.suggestedScope) {
						for (const searchBox of state.searchBoxes) {
							searchBox.value = target.dataset.scopedSuggestion;
						}
						if (dataLayer) {
							dataLayer.push({
								event: "GAEvent",
								eventCategory: "Loop54Search",
								eventAction: "Quick search click",
								eventLabel: `Search suggestion (category: ${target.dataset.suggestedScope}): ${target.dataset.scopedSuggestion}`
							});
						}

						state.params.scopedTerm = target.dataset.suggestedScope;
						state.params.scope = target.dataset.scope;

						waitForFinalEvent(() => {
							handleSearch(true, false);
						}, settings.showSuggestionsAfter, "search");
					}
				});
			}

		}

		const handleArrowDown = (elem) => {
			if (!elem) {
				return false;
			}
			const container = elem.closest(".search-box-wrapper");

			if (container) {
				const suggestion = container.querySelector(".search-suggestions.has-results.visible .suggested-item");
				if (suggestion) {
					suggestion.focus();
				}
			}
			return true;
		};

		if (state.searchBoxDesktop && state.searchBoxMobile) {


			state.searchBoxDesktop.addEventListener("input", (e) => {
				if (state.searchBoxMobile && state.searchBoxMobile.value) {
					state.searchBoxMobile.value = e.target.value;
				}

				waitForFinalEvent(loop54Search.handleSuggestions, settings.showSuggestionsAfter, "quickSearchSuggestions");
			});

			state.searchBoxDesktop.addEventListener("keyup", (e) => {

				if (ignoreKeys.includes(e.key)) {
					return;
				}

				if (state.searchBoxMobile && state.searchBoxMobile.value) {
					state.searchBoxMobile.value = e.target.value;
				}

				switch (e.key) {
					case "Enter":
						newSearchAnalyticsEvent();
						waitForFinalEvent(() => {
							handleSearch(true, true);
						}, settings.showSuggestionsAfter, "search");
						return;
					case "Esc":
					case "Escape":
						loop54Search.clearSuggestions();
						for (const searchBox of state.searchBoxes) {
							searchBox.value = "";
						}
						return;
				}

				waitForFinalEvent(loop54Search.handleSuggestions, settings.showSuggestionsAfter, "quickSearchSuggestions");

			});


			state.searchBoxDesktop.addEventListener("keydown", (e) => {
				switch (e.key) {
					case "Down":
					case "ArrowDown":
						e.preventDefault();
						handleArrowDown(state.searchBoxDesktop);
						return;
				}
			});

		}

		if (state.searchBoxMobile && state.searchBoxDesktop) {
			// Mobile search box acts as salve for desktop search box.
			state.searchBoxMobile.addEventListener("input", (e) => {
				state.searchBoxDesktop.value = e.target.value;
				waitForFinalEvent(loop54Search.handleSuggestions, settings.showSuggestionsAfter, "quickSearchSuggestions");
			});
			state.searchBoxMobile.addEventListener("keyup", (e) => {

				if (ignoreKeys.includes(e.key)) {
					return;
				}

				state.searchBoxDesktop.value = e.target.value;

				switch (e.key) {
					case "Enter":
						newSearchAnalyticsEvent();
						waitForFinalEvent(() => {
							handleSearch(true);
						}, settings.showSuggestionsAfter, "search");
						return;
					case "Esc":
					case "Escape":
						loop54Search.clearSuggestions();
						for (const searchBox of state.searchBoxes) {
							searchBox.value = "";
						}

						return;
				}
				waitForFinalEvent(loop54Search.handleSuggestions, settings.showSuggestionsAfter, "quickSearchSuggestions");

			});

			state.searchBoxMobile.addEventListener("keydown", (e) => {
				switch (e.key) {
					case "Down":
					case "ArrowDown":
						e.preventDefault();
						handleArrowDown(state.searchBoxMobile);
						return;
				}
			});

		}

		if (state.searchBoxes) {

			for (const searchBox of state.searchBoxes) {
				searchBox.addEventListener("focus", (e) => {

					if (dataLayer) {
						dataLayer.push({
							event: "GAEvent",
							eventCategory: "Loop54Search",
							eventAction: "Focus in search field",
							eventLabel: "-"
						});
					}

					for (const suggestionBox of state.suggestions) {
						if (suggestionBox.classList.contains("has-results")) {
							suggestionBox.classList.add("visible");
						} else {
							waitForFinalEvent(loop54Search.handleSuggestions, settings.showSuggestionsAfter, "quickSearchSuggestions");
						}
					}

				});

				const clearSearchButton = searchBox.parentNode.querySelector(".clear-search");

				if (clearSearchButton) {

					clearSearchButton.addEventListener("click", (e) => {

						if (searchBox) {
							searchBox.focus();
						}
						for (const searchBox of state.searchBoxes) {
							searchBox.value = "";
						}
						loop54Search.handleSuggestions();

					});
				}
			}

		}

		if (state.searchButtons) {
			for (const searchButton of state.searchButtons) {
				searchButton.addEventListener("click", () => {
					newSearchAnalyticsEvent();
					waitForFinalEvent(() => {
						handleSearch(true, true);
					}, settings.showSuggestionsAfter, "search");
				});
			}

		}

	}

	initPage(state, settings, loop54Search) {

		if (state.page && state.page.dataset.initialised === "false") {
			state.page.dataset.initialised = "true";
		} else {
			return false;
		}

		if (state.page) {

			if (settings.isCategory) {
				loop54Search.restoreState();
				const getParams = new URLSearchParams(window.location.search);
				state.params.page = parseInt(getParams.get(settings.pageParam)) || false;

				this.addPageListeners(state, settings, loop54Search);


				waitForFinalEvent(() => {
					loop54Search.handleSearch({ clear: true, clearScope: true, keepFilters: false, newSearch: true });
				}, settings.showSuggestionsAfter, "search");
			} else {
				const stateRestored = loop54Search.restoreState();
				const getParams = new URLSearchParams(window.location.search);
				state.params.page = parseInt(getParams.get(settings.pageParam)) || false;

				if (state.restoreState && state.restoreState.activeView && state.activeView !== state.restoreState.activeView) {
					// Restore active view
					if (state.views[state.restoreState.activeView].elems.elem) {
						state.views[state.restoreState.activeView].elems.elem.click();
					}
				}

				this.addPageListeners(state, settings, loop54Search);

				const searchParamValue = String(getParams.get(settings.searchQueryParam)).trim();
				const scopeTermParamValue = String(getParams.get(settings.scopedQueryParam));
				const scopeParamValue = String(getParams.get(settings.scopeParam));

				const searchTerm = sessionStorage.getItem(settings.searchParamKey);
				const searchTermValue = String(searchTerm).trim();

				const searchScopeTerm = sessionStorage.getItem(settings.searchScopeTermKey);
				const searchScope = sessionStorage.getItem(settings.searchScopeKey);

				let query = "";
				let clearScope = true;
				if (getParams.has(settings.searchQueryParam) && searchParamValue.length > 0 && state.searchBoxes.length > 0) {
					query = searchParamValue;
					if (getParams.has(settings.scopedQueryParam) && scopeTermParamValue && getParams.has(settings.scopeParam) && scopeParamValue) {
						state.params.scopedTerm = scopeTermParamValue;
						state.params.scope = scopeParamValue;
						clearScope = false;
					}
				} else if (searchTerm && searchTermValue) {
					query = searchTermValue;
					if (searchScopeTerm && searchScope) {
						state.params.scopedTerm = searchScopeTerm;
						state.params.scope = searchScope;
						clearScope = false;
					}
				}

				if (query) {
					for (const searchBox of state.searchBoxes) {
						searchBox.value = query;
					}
				}

				if (query || stateRestored) {
					waitForFinalEvent(() => {
						loop54Search.handleSearch({ clear: true, clearScope: clearScope, keepFilters: false, newSearch: true });
					}, settings.showSuggestionsAfter, "search");
				}
			}

			for (let view of Object.keys(state.views)) {
				const urlParams = new URLSearchParams(window.location.search);
				const orderBy = urlParams.get(settings.orderParam);
				if (state.views[view].elems.orderBy) {
					if (state.restoreState && state.restoreState.views[view] && state.restoreState.views[view].orderBy && state.restoreState.views[view].orderBy.length) {
						const optionValue = state.restoreState.views[view].orderBy[0].attributeName + "-" + state.restoreState.views[view].orderBy[0].order;
						state.views[view].elems.orderBy.value = optionValue;
					} else if (settings.isCategory) {
						if (orderBy && ((settings.views[view]?.sortOptions || []).includes(orderBy) || (orderBy === "default"))) {
							if (orderBy === "default") {
								state.views[view].elems.orderBy.value = "";
							} else {
								state.views[view].elems.orderBy.value = orderBy;
							}
						}
					}

					if (orderBy && settings.isCategory) {
						state.views[view].loop54FiltersHandler.updateRobotsMetaTag(true);
					}

					state.views[view].elems.orderBy.addEventListener("change", (e) => {
						waitForFinalEvent(() => {
							loop54Search.handleSearch({ activeView: view, clear: true, clearScope: false, keepFilters: true, newSearch: false });
						}, settings.updateFiltersAfter, "searchFiltersSelected");
					});
				}

				if (state.views[view].elems.searchFacets) {

					if (!state.views[view].elems.searchFacets.dataset.selectedFacets) {
						state.views[view].elems.searchFacets.dataset.selectedFacets = "{}";
					}

					const observer = new MutationObserver((mutationsList) => {
						for (const mutation of mutationsList) {

							// If nothing changed, do nothing.
							if (mutation.oldValue === mutation.target.getAttribute(mutation.attributeName)) {
								continue;
							}


							switch (mutation.attributeName) {
								case "data-selected-facets":
									if (state.views[view].elems.searchFacets) {
										if (state.views[view].elems.searchFacets.dataset.skipFacetUpdate !== "true") {
											waitForFinalEvent(() => {
												loop54Search.handleSearch({ activeView: view, clear: true, clearScope: true, keepFilters: true, newSearch: false });
											}, settings.updateFiltersAfter, "facetSearch");
										} else {
											state.views[view].elems.searchFacets.dataset.skipFacetUpdate = false;
										}
									}

									break;

							}
						}
					});

					// Start observing the target node for configured mutations
					observer.observe(state.views[view].elems.searchFacets, { attributes: true, childList: false, subtree: false, attributeOldValue: true });

					const fixDropdownPosition = (el) => {
						throttle(() => {
							const dropdown = el.querySelector(".facet-options");
							if (dropdown) {
								const rect = dropdown.getBoundingClientRect();
								if (rect.left < 0) {
									el.classList.remove("revert");
								} else if ((rect.right + 20) > (window.innerWidth || document.documentElement.clientWidth)) {
									el.classList.add("revert");
								}
							}
						}, 100, "fixDropdown");
					};

					window.addEventListener("resize", (e) => {
						const dropdown = state.views[view].elems.content.querySelector(".facet-option.open");
						if (dropdown) {
							fixDropdownPosition(dropdown);
						}
					}, { passive: true });

					state.views[view].elems.searchFacets.addEventListener("click", (e) => {
						const clickTarget = e.target;
						const clickedButton = clickTarget.closest("button[data-selected-facet], button[data-clear-facets]");
						if (clickedButton) {

							const clearFacets = clickedButton.dataset.clearFacets === "true" ? true : false;

							const actionObject = {
								action: "removeFacet",
								clearAll: clearFacets,
								facet: clickedButton.dataset.facet,
								value: clickedButton.dataset.value,
								checked: false
							};

							const selectedFacets = state.views[view].elems.searchFacets.querySelector(".js-selected-facets-bar");
							if (clearFacets) {
								if (selectedFacets) {
									selectedFacets.classList.remove("has-facets");
								}
							} else {
								clickedButton.remove();
								if (state.views[view].elems.searchFacets.querySelectorAll("button[data-selected-facet]").length === 0) {
									if (selectedFacets) {
										selectedFacets.classList.remove("has-facets");
									}
								}
							}


							// waitForFinalEvent(() => {
							state.views[view].loop54FiltersHandler.updateFacets(actionObject);
							// }, settings.updateFiltersAfter, "searchFiltersSelected");


						} else if (clickTarget.closest("input[data-is-facet]")) {
							const actionObject = {
								action: "facet",
								facet: clickTarget.dataset.facet,
								value: clickTarget.dataset.value,
								checked: clickTarget.checked
							};
							// waitForFinalEvent(() => {

							state.views[view].loop54FiltersHandler.updateFacets(actionObject);
							// }, settings.updateFiltersAfter, "searchFiltersSelected");
						} else {
							const facetOptionsClick = clickTarget.closest(".open-facet-options");

							if (facetOptionsClick) {
								const facetOption = clickTarget.closest(".facet-option");

								if (facetOption) {
									facetOption.classList.remove("revert");
									if (facetOption.classList.contains("open")) {
										state.views[view].loop54FiltersHandler.closeFilterDropdowns();
									} else {

										const facetsBar = facetOptionsClick.closest(".js-facets-bar");
										if (facetsBar) {
											facetsBar.scrollTo({ left: facetOption.offsetLeft - 20, behavior: "smooth" });
										}
										facetOption.classList.add("open");
										document.body.classList.add("prevent-scroll-mobile");
										fixDropdownPosition(facetOption);
										state.views[view].loop54FiltersHandler.filterDropdownListener();
									}
								}

								for (const openDropdown of state.views[view].elems.searchFacets.querySelectorAll(".facet-option.open")) {
									if (openDropdown !== facetOption) {
										openDropdown.classList.remove("open");
									}
								}
							}


						}
					});

				}
			}
		}
	}

	init() {
		const loop54Search = new Loop54Search();

		const state = loop54Search.state;
		const settings = loop54Search.settings;

		const categoryPage = document.querySelector(settings.pageContainers.category);

		if (categoryPage) {
			settings.isCategory = true;
			settings.loop54Mode = "category";
			state.views = { products: state.views.products };
		}

		loop54Search.getElements();

		if (!settings.endpoint) {
			return false;
		}

		if (settings.loop54Mode !== "category") {
			tabs(".m-tabs");
		}


		this.addSearchBoxListeners(state, settings, loop54Search);

		this.initPage(state, settings, loop54Search);

		const mobileSearchContainer = document.querySelector(".m-search-container--mobile");
		let lastScrollTop = 0;
		let scrollDirection = 0;

		// the throttle function in this file didn't work for some reason
		window.addEventListener("scroll", simpleThrottle(() => {
			let st = window.scrollY || document.documentElement.scrollTop;
			if (st > lastScrollTop) {
				scrollDirection = -1;
			} else {
				scrollDirection = 1;
			}
			lastScrollTop = st <= 0 ? 0 : st;

			if (mobileSearchContainer) {
				const hideMobileSearch = scrollDirection === -1 && st > 75;
				if (hideMobileSearch) {
					mobileSearchContainer.classList.add("hide-mobile-search");
				} else {
					mobileSearchContainer.classList.remove("hide-mobile-search");
				}
			}
		}, 100, "detectScrollDirection"));

		return true;

	}
}

export default Loop54SearchUi;
